// Volunteering, and stepping back (bead hf-e6-commitments-coverage-udt.2).
//
// Pure components. The claim outcome — including the race loss this product cares
// most about getting right — already belongs to `features/claim`, and this file uses
// it rather than growing a second one.
//
// ── THIS BEAD IMPLEMENTS NO CLAIMING LOGIC ──────────────────────────────────────
//
// The bead says so outright, and it is worth restating where somebody might be
// tempted. Nothing here checks capacity before submitting, compares a revision, or
// decides whether a claim would succeed. Atomic claiming is the crown jewel and it
// lives in one transaction in `convex/domain/claimGuard.ts`; a browser that
// pre-checked would be a second, slower, wrong copy of it — and the window between
// its check and the server's is exactly where the double-booking lives.
//
// So the offer is shown whenever the server says this person MAY offer, the tap is
// sent, and whatever comes back is rendered verbatim through the typed vocabulary.
//
// ── THE REJECTION WORDS ARE NOT PARAPHRASED ─────────────────────────────────────
//
// Every sentence comes from `@herefor/product-language`, selected by
// `features/claim`'s `messageFor`, which is total over `CommitmentRejection`. Nothing
// here composes a sentence about a refusal, and nothing here decides what a refusal
// means. A paraphrase would be a second copy of reviewed wording, drifting.
//
// ── AVAILABILITY IS NEVER CONSENT ───────────────────────────────────────────────
//
// Invariant 3, AVL-019. There is no affordance in this file that turns an offer of
// time into a commitment, and no wording that suggests one exists. The only way to
// become responsible for something is the explicit tap below.

import { Button, Dialog, Select, StatusMessage } from "@herefor/design-system";
import {
  ACTION_LABELS,
  COMMITMENT_MESSAGES,
  VOLUNTEER_SURFACE_MESSAGES as COPY,
  WITHDRAWAL_REASON_CODES,
  WITHDRAWAL_REASON_LABELS,
} from "@herefor/product-language";
import { useState } from "react";

import { ClaimOutcome, messageFor, type AdjacentNeed, type ClaimState } from "../claim";
import { ProxyProvenance } from "./ProxyCommitmentSurfaces";
import type { WithdrawalReasonCode, WithdrawState } from "./useWithdraw";

/* ============================================================== the offer === */

export interface VolunteerProps {
  /** What the person would be taking on, already worded by the caller. */
  readonly responsibility: string;
  /**
   * Whether the SERVER says this person may offer. Never inferred here.
   *
   * False covers both "you lack the capability" and "there is nothing to offer for" —
   * the surface does not distinguish them, because it does not need to and because
   * distinguishing them in the client would mean deciding one of them here.
   */
  readonly canOffer: boolean;
  /** Connectivity verdict. Unlike capability denial, this keeps the action visible. */
  readonly actionsDisabled?: boolean;
  readonly state: ClaimState;
  readonly onOffer: () => void;
  readonly onViewPlan?: () => void;
  readonly stillNeeded?: readonly AdjacentNeed[];
}

/**
 * "I can do this."
 *
 * The consequence is stated BEFORE the tap, not after: COM-003 wants the person to
 * know their name will be visible to the family, and learning that from a
 * confirmation screen is learning it too late to decline.
 */
export function VolunteerOffer({
  responsibility,
  canOffer,
  actionsDisabled = false,
  state,
  onOffer,
  onViewPlan,
  stillNeeded,
}: VolunteerProps) {
  const settled = state.status === "accepted" || state.status === "refused";

  return (
    <div className="volunteer">
      {canOffer && !settled ? (
        <>
          <p className="volunteer__consequence">
            {COMMITMENT_MESSAGES.consequence(responsibility)}
          </p>
          <Button
            variant="primary"
            onClick={onOffer}
            disabled={actionsDisabled || state.status === "submitting"}
          >
            {state.status === "submitting" ? COPY.working : ACTION_LABELS.commit}
          </Button>
        </>
      ) : null}

      {/* The outcome, including the race loss — `features/claim` owns that moment and
          this bead does not second-guess it. */}
      <ClaimOutcome
        state={state}
        {...(onViewPlan === undefined ? {} : { onViewPlan })}
        {...(stillNeeded === undefined ? {} : { stillNeeded })}
      />

      {/* THE REFRESH the bead asks for beside a stale rejection. The sentence is
          REJECTION_MESSAGES.STALE_REVISION, rendered by ClaimOutcome above; this is
          the control that acts on it. */}
      {state.status === "refused" && isStale(state) && onViewPlan !== undefined ? (
        <Button variant="secondary" onClick={onViewPlan}>
          {COPY.showLatest}
        </Button>
      ) : null}
    </div>
  );
}

function isStale(state: Extract<ClaimState, { status: "refused" }>): boolean {
  return state.rejection.kind === "revision_stale" || state.rejection.kind === "needs_recheck";
}

/* ========================================================== stepping back === */

export interface WithdrawProps {
  /** Whether the server says this commitment is this person's to withdraw (COM-010). */
  readonly canWithdraw: boolean;
  /** Connectivity verdict. Unlike capability denial, this keeps the action visible. */
  readonly actionsDisabled?: boolean;
  readonly state: WithdrawState;
  readonly onWithdraw: (reasonCode?: WithdrawalReasonCode) => void;
  readonly onViewPlan?: () => void;
}

/**
 * "I can't do this now."
 *
 * §10.10: withdrawing does not demand an excuse. The reason is optional in the label,
 * optional in the type, and the dialog says so again in a sentence — because a person
 * looking at a picker under a heading about withdrawing will assume it is required
 * unless told otherwise, and hesitate at exactly the wrong moment.
 */
export function WithdrawCommitment({
  canWithdraw,
  actionsDisabled = false,
  state,
  onWithdraw,
  onViewPlan,
}: WithdrawProps) {
  const [confirming, setConfirming] = useState(false);
  const [reasonCode, setReasonCode] = useState<string>("");

  if (state.status === "withdrawn") {
    return (
      <div className="volunteer" data-withdraw-outcome="withdrawn">
        {/* Neutral and final. It says what is true — they are no longer marked, and
            the family can find somebody else — and nothing about letting anyone down
            (COM-013). */}
        <StatusMessage tone="info">{COMMITMENT_MESSAGES.withdrawn}</StatusMessage>
      </div>
    );
  }

  if (state.status === "refused") {
    const message = messageFor(state.rejection);
    return (
      <div
        className="volunteer"
        data-withdraw-outcome="refused"
        data-withdraw-rejection={state.rejection.kind}
      >
        <StatusMessage tone="info">
          <div className="volunteer__lines">
            <p className="volunteer__title">{message.title}</p>
            <p className="volunteer__body">{message.body}</p>
          </div>
        </StatusMessage>
        {onViewPlan === undefined ? null : (
          <Button variant="secondary" onClick={onViewPlan}>
            {message.action}
          </Button>
        )}
      </div>
    );
  }

  if (!canWithdraw) return null;

  return (
    <div className="volunteer">
      <Button
        variant="secondary"
        onClick={() => {
          setConfirming(true);
        }}
        disabled={actionsDisabled || state.status === "submitting"}
      >
        {state.status === "submitting" ? COPY.working : COPY.withdraw}
      </Button>

      <Dialog
        open={confirming}
        title={COPY.withdrawTitle}
        onClose={() => {
          setConfirming(false);
        }}
        footer={
          <>
            <Button
              variant="primary"
              disabled={actionsDisabled}
              onClick={() => {
                setConfirming(false);
                onWithdraw(reasonCode === "" ? undefined : (reasonCode as WithdrawalReasonCode));
              }}
            >
              {COPY.withdrawConfirm}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setConfirming(false);
              }}
            >
              {COPY.withdrawKeep}
            </Button>
          </>
        }
      >
        {/* Invariant 6: says what survives, so nobody thinks stepping back erases the
            record of what was agreed. */}
        <p>{COPY.withdrawBody}</p>
        <p>{COPY.withdrawNoReasonNeeded}</p>
        <Select
          label={COPY.withdrawReasonLabel}
          value={reasonCode}
          onChange={(changeEvent) => {
            setReasonCode(changeEvent.target.value);
          }}
          options={[
            { value: "", label: COPY.withdrawReasonUnset },
            ...WITHDRAWAL_REASON_CODES.map((code) => ({
              value: code,
              label: WITHDRAWAL_REASON_LABELS[code],
            })),
          ]}
        />
      </Dialog>
    </div>
  );
}

/* ============================================== who is helping, and who is not === */

export interface CommitmentStatusProps {
  /**
   * The OTHER people already down to help, as the server derived them.
   *
   * The viewer is excluded by the caller rather than filtered out here, because only
   * the caller knows which person the viewer is — and a component that guessed would
   * either name somebody to themselves or hide a real helper who shares a name.
   */
  readonly others: readonly {
    readonly personId: string;
    readonly name: string;
    readonly recordedByName: string | null;
    readonly agreementSource: string | null;
  }[];
  /** True when the viewer is one of the people helping. */
  readonly includesYou: boolean;
  /** True while the need still wants somebody — server-derived, never counted here. */
  readonly stillNeedsSomeone: boolean;
}

/** A plain statement of where things stand, above the offer. */
export function CommitmentStatus({
  others,
  includesYou,
  stillNeedsSomeone,
}: CommitmentStatusProps) {
  return (
    <div className="volunteer__status">
      {includesYou ? <p>{COPY.alreadyHelping}</p> : null}
      {others.map((person) => (
        <p key={person.personId}>
          {person.recordedByName === null ? (
            COPY.someoneElseHelping(person.name)
          ) : (
            <ProxyProvenance
              helperName={person.name}
              recorderName={person.recordedByName}
              agreementSource={person.agreementSource ?? ""}
            />
          )}
        </p>
      ))}
      {/* Derived by the server through the kernel. The browser never decides whether
          a need still needs somebody (COV-002, invariant 1). */}
      {stillNeedsSomeone ? <p>{COPY.helpNeededHere}</p> : null}
    </div>
  );
}
